import * as React from 'react';
import { Header, Form, Button, Modal, Icon, Tab } from 'semantic-ui-react';
import { ChartType } from 'src/services/graphql/Models';
import uuidv1 from 'uuid/v1';
import { ChartInterface, chartTypeOptions } from './shared';
import TriggerableStats from 'src/container/TriggerableStats/TriggerableStatEdit';
import Convert from 'convert-units';
import ClientDropDown from 'src/container/Client/ClientOptions';
import getDurationOptions from 'src/components/inputs/DurationInput';
import SensorTypeSelection from 'src/container/SensorType/SensorTypeOptions';
import UnitOptions from 'src/container/Unit/UnitOptions';
import UnitGroupOptions from 'src/container/UnitGroup/UnitGroupOptions';
import FilterInput from 'src/components/inputs/FilterInput';
import Chart from 'src/stores/application/classes/Chart';
import { inject, observer } from 'mobx-react';
import TriggerableStat from 'src/stores/application/classes/TriggerableStat';
import ChartView from './ChartView';
import { AuthStore } from 'src/stores/authStore';
import TriggerNotificationInput from '../TriggerableStats/TriggerNotificationEdit';

interface DefaultChartProps {
    fromObject: Partial<ChartInterface>;
    resolveRow: (chart?: ChartInterface) => any;
    rejectRow: (err?: Error | { isNormal: boolean, typedId: number }) => any;
    isNew?: boolean;
    authStore?: AuthStore;
    triggerableStats?: any;
    routing?: any;
}

@inject('triggerableStats', 'authStore', 'routing')
@observer
export default class DefaultChart extends React.Component<DefaultChartProps, any> {
    handleChange: any;
    resolveRow?: (chart?: ChartInterface) => any;
    rejectRow?: (err?: any) => any;
    activeIndex: number = 0;
    measurementOptions: any[] = [];
    actions: any[] = [];
    submitting: boolean = false;

    constructor(props: DefaultChartProps) {
        super(props);
        const { resolveRow, rejectRow, fromObject } = props;

        this.resolveRow = resolveRow;
        this.rejectRow = rejectRow;
        this.state = {
            ...fromObject, 
            typedId: fromObject.typedId || `ch:${uuidv1()}`
        };
        this.handleChange = this._handleChange.bind(this);
    }

    changeIndex = v => { this.activeIndex = v; this.forceUpdate(); };
    handleTabChange = (e, { activeIndex }) => this.changeIndex(activeIndex);

    render() {
        const { goBack } = this.props.routing;
        const submitHandler = this.handleSubmit.bind(this);
        const panes = [
            { menuItem: 'View Settings', render: this.mainForm.bind(
                this,
                this,
                this.state,
                this.props
            ) },
            { 
                menuItem: 'Displayed Unit and Sensors',
                render: this.renderUnitSensorInput.bind(this, this, this.state) 
            },
            { menuItem: 'Displayed Stats', render: this.renderStatInput.bind(this, this, this.state) },
            { menuItem: 'Triggers', render: this.renderTriggerInput.bind(this, this, this.state) },
        ];
        
        if (!this.props.isNew) {
            
            panes.unshift({
                menuItem: 'Chart view ',
                render: this.renderChart.bind(
                    this,
                    this,
                    this.state,
                    this.props
                )
            });
        }
        return (
            <Modal 
                closeIcon
                size="fullscreen"
                open={true}
                onClose={this.handleClose.bind(this)}
                className="standard-padding center-margin tb-margin"
                style={{
                    marginTop: 10, 
                    marginLeft: 'auto', 
                    marginRight: 'auto',
                    padding: '21px',
                    boxSizing: 'content-box',
                    overflowY: 'scroll'
                }}
                dimmer="inverted"
                closeOnDimmerClick={true}
            >
                <Modal.Header>
                    <Button 
                        type="button"
                        labelPosition="left" 
                        icon="left chevron" 
                        content="Back" 
                        onClick={() => goBack()}
                        style={{
                            marginRight: '33px',
                            position: 'relative',
                            top: '-2px'
                        }}
                    />Editing Chart {this.state.name}
                </Modal.Header>
                <Form 
                    onSubmit={submitHandler}
                >   
                    <Modal.Content>
                        <Modal.Description className="top-margin">                      
                            <Tab 
                                panes={panes} 
                                activeIndex={this.activeIndex} 
                                onTabChange={this.handleTabChange.bind(this)}
                            />
                        </Modal.Description>
                    </Modal.Content>
                    {(this.activeIndex === 0 && this.props.isNew) ?
                        <Modal.Actions>
                            <Button 
                                type="button"
                                onClick={() => { this.changeIndex(1); }} 
                                primary 
                                float="right" 
                                className="top-margin"
                            >
                                Next <Icon name="chevron right"/>
                            </Button>
                        </Modal.Actions>
                        : 
                        <Modal.Actions>
                            <Form.Button primary float="right" className="top-margin" disabled={this.state.submitted}>
                                Submit <Icon name="chevron right"/>
                            </Form.Button>
                        </Modal.Actions>
                    }
                </Form>
            </Modal>
        );
    }

    _handleChange(
        e: any, 
        { 
            name, value 
        }: {
            name: string, 
            value: any
        }
    ) {
        if (name.includes('.')) {
            this.setState(this.reduce(this.state, name.split('.'), value));
        } else {
            this.setState({ [name]: value });
        }
    }

    reduce(object: any, names: string[], value: any) {
        let result = {...object};
        let name = names.shift();
        if (names.length > 0) {
            result[name] = this.reduce(result[name] || {}, names, value);
        } else {
            result[name] = value;
        }
    }

    registerSaveCallback (saveFunction: any) {
        this.setState({
            saveCallback: saveFunction
        });
    }

    handleSubmit = (event: any) => {
        if (event.keyCode !== 13) {
            const rowResolver = this.resolveRow.bind(this, new Chart(this.state));
            const me = this;
            if (this.state.saveCallback) {
                new Promise((resolveSave) => { 
                    me.state.saveCallback();
                    this.setState({ 
                        submitted: true,
                        saveCallback: undefined
                    });
                    this.checkAnyTriggerableStats(resolveSave);

                }).then(() => { 
                    rowResolver(); 
                }); 
            } else {
                rowResolver();
            }
            
        }
    }

    checkAnyTriggerableStats(resolveSave: any) {
        const triggerableStats = this.props.triggerableStats;
        const allItems = triggerableStats.getParsedItems as TriggerableStat[];
        const chartItems = allItems.filter(o => o.associatedChart === this.state.typedId);
        
        if (chartItems.length > 0) {
            resolveSave();
        } else {
            setTimeout(this.checkAnyTriggerableStats.bind(this, resolveSave), 500);
        }

    }

    handleClose = () => {
        this.rejectRow({ isNormal: true, typedId: this.state.typedId});
    }

    handleChecked (e: any, {checked, name}: any) {
        this.setState({
            [name] : checked
        });
    }

    mainForm(
        {
            handleChange,
            handleChecked,
            measurementOptions,
            actions
        }: any,
        { 
            name,
            durationFromType,
            durationFrom,
            chartType,
            clientId,
            sensorType,
            measurementUnit,
            units,
            unitGroups,
            include,
            exclude,
            breakOnError,
            geospread
        }: any,
        {
            authStore,
            isNew
        }: any
    ) {
        return (
            <Tab.Pane>
                {(authStore.isSuperUser && isNew) ?
                    (
                        <Form.Field>
                            <label>Chart's Client</label>
                            <ClientDropDown 
                                placeholder="Chart's Client" 
                                name="clientId" 
                                defaultValue={clientId} 
                                onChange={handleChange}
                                required
                            />
                        </Form.Field>
                    ) : null
                }
                <Form.Field>
                    <label>Name</label>
                    <Form.Input 
                        required
                        placeholder="Name" 
                        name="name" 
                        defaultValue={name} 
                        onChange={handleChange} 
                    />
                </Form.Field>

                <Form.Field>
                    <label>Chart Type</label>
                    <Form.Select 
                        required
                        options={chartTypeOptions}
                        placeholder="Chart Type" 
                        name="chartType" 
                        defaultValue={chartType} 
                        onChange={handleChange} 
                    />
                </Form.Field>
                
                <Form.Field>        
                    <br/>
                    <label>Spread chart on map by default</label>
                    <Form.Checkbox 
                        toggle
                        name="geospread" 
                        defaultChecked={geospread}
                        onChange={handleChecked.bind(this)} 
                    />
                </Form.Field>

                {isNew ? getDurationOptions(
                    {durationFromType, durationFrom},
                    handleChange,
                    actions
                ) : null}

                <Form.Field>
                    <label>Sensor Type</label>
                    <SensorTypeSelection
                        placeholder="SensorType" 
                        name="sensorType"
                        handleChange={handleChange}
                        sensorTypeChangeHandler={this.sensorTypeChange.bind(this)}
                        defaultValue={sensorType} 
                    />
                </Form.Field>

                {(measurementOptions && measurementOptions.length > 0) ? 
                    (
                        <Form.Field>
                            <label>Measurement Unit</label>
                            <Form.Select 
                                required
                                disabled={measurementOptions.length <= 0}
                                options={measurementOptions}
                                placeholder="Measurement Unit" 
                                name="measurementUnit" 
                                defaultValue={measurementUnit} 
                                onChange={handleChange} 
                            />
                        </Form.Field>
                    )
                    : null
                }
            
                {chartType === ChartType.line ? 
                    (
                        <Form.Field>        
                            <br/>
                            <label>Break Line On Error</label>
                            <Form.Checkbox 
                                toggle 
                                name="breakOnError" 
                                defaultChecked={breakOnError}
                                onChange={handleChecked.bind(this)} 
                            />
                        </Form.Field>
                    ) :
                    undefined
                }
            </Tab.Pane>
        );
    }

    renderChart(
        { handleChange, actions }: any,
        chart: any,
        { isNew }: any
    ) {
        const { durationFromType, durationFrom } = chart;
        const chartObj = new Chart(chart);
        return (
            <Tab.Pane>
                <ChartView
                    chart={chartObj}
                    geospread={chartObj.geospread}
                    lockDetails={true}
                />
                <hr/>
                <br/>
                {isNew ? null : getDurationOptions(
                    { durationFromType, durationFrom },
                    handleChange,
                    actions
                )}
            </Tab.Pane>
        );

    }

    renderUnitSensorInput({ handleChange }: any, {include, exclude}: any) {
        return (
            <Tab.Pane>
            <div>
                <Header as="h3">Include</Header>
                <hr/>
                <FilterInput 
                    onChange={handleChange}
                    name="include"
                    initialValue={include}
                />
            </div>
            <hr/>
            <div>
                <Header as="h3">Exclude</Header>
                <hr/>
                <FilterInput 
                    onChange={handleChange}
                    name="exclude"
                    initialValue={exclude}
                />
            </div>
            </Tab.Pane>
        );
    }

    renderFilterInput (
        { handleChange }: any,
        { unitIds, unitGroups, unitSensors, clientId }: any, 
        { name, title }: any
    ) {
        return ( 
            <div>
                <Header as="h1"> {title}</Header>
                <hr/>
                <Form.Field>  
                    <label>Unit Groups</label>
                    <UnitGroupOptions
                        multiple
                        onChange={handleChange}
                        name={`${name}.unitIds`}
                        defaultValue={unitGroups} 
                        clientId={clientId}
                    />
                </Form.Field>
                <Form.Field>
                    <label>Unit</label>
                    <UnitOptions
                        multiple
                        placeholder="Units" 
                        name={`${name}.unitGroups`}
                        defaultValue={unitIds}
                        onChange={handleChange}
                        clientId={clientId}
                    />
                </Form.Field>
                <Form.Field>  
                    <label>Unit Sensor</label>
                    <Form.Input
                        onChange={handleChange}
                        name={`${name}.unitSensors`}
                        defaultValue={unitSensors}
                    />
                </Form.Field> 
            </div>
        );
    }

    renderStatInput(
        {
            registerSaveCallback,
            removeSaveCallback
        }: any,
        {
            clientId,
            typedId,
            chartType
        }: any
    ) {
        return (
            <Tab.Pane>
                <TriggerableStats 
                    clientId={clientId}
                    chartId={typedId}
                    registerSaveCallback={registerSaveCallback}
                    removeSaveCallback={removeSaveCallback}
                    couldOnlyHaveOneTypeofValue={chartType !== ChartType.table}
                />
            </Tab.Pane>
        );
    }
    
    renderTriggerInput(
        {
            registerSaveCallback,
            removeSaveCallback
        }: any,
        {
            clientId,
            typedId,
            chartType
        }: any
    ) {
        return (
            <Tab.Pane>
                <TriggerNotificationInput
                    clientId={clientId}
                    chartId={typedId}
                    registerSaveCallback={registerSaveCallback}
                    removeSaveCallback={removeSaveCallback}
                    couldOnlyHaveOneTypeofValue={chartType !== ChartType.table}
                />
            </Tab.Pane>
        );
    }
    
    sensorTypeChange(e: any, {name, value}: any) {
        if (!value) {
            this.measurementOptions = [];
            this.actions = [];
            this.setState({[name]: undefined});
            return;
        }
        
        this.measurementOptions = Convert().possibilities(value.measurement)
            .map((unit) => {
                return {
                    key: unit,
                    value: unit,
                    text: unit
                };
            });

        this.actions  = value.possibleActions;
        this.handleChange(e, { name, value: value.typedId});
    }

}


// WEBPACK FOOTER //
// ./src/container/Chart/ChartEdit.tsx